import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'self-baseline' | 'partner-baseline' | 'relationship-context' | 'friction-loop' | 'system-stability';
export function RelationshipDecodeInputs() {
  const navigate = useNavigate();
  const [showEntry, setShowEntry] = useState(true);
  const [currentPhase, setCurrentPhase] = useState<Phase>('self-baseline');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [formData, setFormData] = useState({
    // Self Baseline
    fullName: '',
    dateOfBirth: '',
    timeOfBirthOption: '',
    timeOfBirth: '',
    birthCity: '',
    birthCountry: '',
    // Partner Baseline
    partnerName: '',
    partnerDob: '',
    partnerTimeOfBirthOption: '',
    partnerTimeOfBirth: '',
    partnerBirthCity: '',
    partnerBirthCountry: '',
    // Relationship Context
    relationshipType: '',
    // Core Friction Loop
    trigger: '',
    yourResponse: [] as string[],
    theirResponse: '',
    outcome: '',
    // System Stability
    duration: '',
    underlyingPressure: ''
  });
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleMultiSelect = (field: string, value: string) => {
    setFormData(prev => {
      const current = prev[field as keyof typeof prev] as string[];
      if (current.includes(value)) {
        return {
          ...prev,
          [field]: current.filter(v => v !== value)
        };
      } else if (current.length < 2) {
        return {
          ...prev,
          [field]: [...current, value]
        };
      }
      return prev;
    });
  };
  const handleStartDiagnostic = () => {
    setShowEntry(false);
  };
  const handleNext = () => {
    // Self Baseline navigation
    if (currentPhase === 'self-baseline') {
      if (!formData.fullName || !formData.dateOfBirth || !formData.timeOfBirthOption || !formData.birthCity || !formData.birthCountry) return;
      if ((formData.timeOfBirthOption === 'exact' || formData.timeOfBirthOption === 'approximate') && !formData.timeOfBirth) return;
      setCurrentPhase('partner-baseline');
      setCurrentStep(1);
    }
    // Partner Baseline navigation
    else if (currentPhase === 'partner-baseline') {
      if (currentStep === 1 && !formData.partnerName) return;
      if (currentStep === 2 && (!formData.partnerDob || !formData.partnerTimeOfBirthOption)) return;
      if (currentStep === 2 && (formData.partnerTimeOfBirthOption === 'exact' || formData.partnerTimeOfBirthOption === 'approximate') && !formData.partnerTimeOfBirth) return;
      if (currentStep === 3 && (!formData.partnerBirthCity || !formData.partnerBirthCountry)) return;
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase('relationship-context');
        setCurrentStep(1);
      }
    }
    // Relationship Context navigation
    else if (currentPhase === 'relationship-context') {
      if (!formData.relationshipType) return;
      setCurrentPhase('friction-loop');
      setCurrentStep(1);
    }
    // Friction Loop navigation
    else if (currentPhase === 'friction-loop') {
      if (currentStep === 1 && !formData.trigger) return;
      if (currentStep === 2 && formData.yourResponse.length === 0) return;
      if (currentStep === 3 && !formData.theirResponse) return;
      if (currentStep === 4 && !formData.outcome) return;
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase('system-stability');
        setCurrentStep(1);
      }
    }
    // System Stability navigation
    else if (currentPhase === 'system-stability') {
      if (currentStep === 1 && !formData.duration) return;
      if (currentStep === 2 && !formData.underlyingPressure) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowEndScreen(true);
      }
    }
  };
  const handleGenerateReport = () => {
    setIsGenerating(true);
    console.log('Relationship Decode inputs:', formData);
    setTimeout(() => {
      navigate('/relationship-decode-output');
    }, 3000);
  };
  // Options
  const relationshipTypeOptions = ['Romantic partner', 'Spouse / marriage', 'Long-term dating', 'Former partner', 'Emotionally close connection'];
  const triggerOptions = ['Specific person or behavior', 'Type of situation', 'Time pressure', 'Uncertainty', 'Responsibility or expectations'];
  const yourResponseOptions = ['Explain or justify', 'Withdraw', 'Accommodate', 'Push harder', 'Escalate emotionally'];
  const theirResponseOptions = ['Avoid or deflect', 'Push back', 'Shut down', 'Control the frame', 'Seek reassurance'];
  const outcomeOptions = ['Temporary peace', 'Emotional distance', 'Repeated conflict', 'Unresolved tension', 'Short-term closeness, long-term drift'];
  const durationOptions = ['Recent', 'Months', 'Years', 'On and off'];
  const underlyingPressureOptions = ['Fear', 'Inadequacy', 'Guilt', 'Resentment', 'Frustration'];
  if (showEntry) {
    return <>
        <Helmet data-id="element-3010">
          <title data-id="element-3011">Relationship Decode — Diagnostic Input | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-3012" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-3013">
          <header data-id="element-3014">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3015">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3016">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3017">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3018" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-3019">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-3020">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-3021">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-3022">
                Relationship Decode — diagnostic input
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed max-w-xl mx-auto" data-id="element-3023">
                Answering these helps map the interaction system accurately.
                <br data-id="element-3024" />
                This is not an evaluation of individuals.
              </p>
              <button onClick={handleStartDiagnostic} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3025">
                Start Diagnostic
              </button>
            </div>
          </main>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-3026">
          <title data-id="element-3027">
            Generating Report — Relationship Decode | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-3028" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-3029">
          <div className="text-center" data-id="element-3030">
            <p className="text-xl text-[#F5F5F5]/70" data-id="element-3031">
              Analyzing interaction patterns...
            </p>
          </div>
        </div>
      </>;
  }
  if (showEndScreen) {
    return <>
        <Helmet data-id="element-3032">
          <title data-id="element-3033">
            Ready to Generate — Relationship Decode | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-3034" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-3035">
          <header data-id="element-3036">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3037">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3038">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3039">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3040" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-3041">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-3042">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-3043">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-3044">
                Ready to generate your Relationship Decode
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed" data-id="element-3045">
                Interaction patterns and constraints have been captured.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3046">
                <button onClick={handleGenerateReport} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3047">
                  Generate Full Report
                </button>
                <button onClick={() => {
                setShowEndScreen(false);
                setCurrentPhase('self-baseline');
                setCurrentStep(1);
              }} className="px-8 py-5 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3048">
                  Edit responses
                </button>
              </div>
            </div>
          </main>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-3049">
        <title data-id="element-3050">Relationship Decode — Diagnostic Input | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-3051" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-3052">
        <header data-id="element-3053">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3054">
            <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-3055">
              <p className="text-xs text-[#F5F5F5]/50 text-center tracking-wider" data-id="element-3056">
                {currentPhase === 'self-baseline' && 'Your baseline'}
                {currentPhase === 'partner-baseline' && 'Partner baseline'}
                {currentPhase === 'relationship-context' && 'Relationship context'}
                {currentPhase === 'friction-loop' && `Friction loop — Step ${currentStep} of 4`}
                {currentPhase === 'system-stability' && `System stability — Step ${currentStep} of 2`}
              </p>
            </div>
          </nav>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-3057">
          <div className="max-w-2xl mx-auto w-full" data-id="element-3058">
            {/* Self Baseline */}
            {currentPhase === 'self-baseline' && <div data-id="element-3059">
                <h1 className="text-3xl md:text-4xl font-medium mb-4 text-center leading-tight tracking-tight" data-id="element-3060">
                  Your baseline
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-3061">
                  This establishes your baseline response architecture.
                  <br data-id="element-3062" />
                  It is not used for prediction.
                </p>

                <div className="space-y-6 mb-12" data-id="element-3063">
                  <div data-id="element-3064">
                    <label htmlFor="fullName" className="block text-sm font-medium mb-2" data-id="element-3065">
                      Full Name
                    </label>
                    <input type="text" id="fullName" value={formData.fullName} onChange={e => handleInputChange('fullName', e.target.value)} placeholder="Your full name" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3066" />
                  </div>

                  <div data-id="element-3067">
                    <label htmlFor="dateOfBirth" className="block text-sm font-medium mb-2" data-id="element-3068">
                      Date of Birth
                    </label>
                    <input type="date" id="dateOfBirth" value={formData.dateOfBirth} onChange={e => handleInputChange('dateOfBirth', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3069" />
                  </div>

                  <div data-id="element-3070">
                    <label htmlFor="timeOfBirthOption" className="block text-sm font-medium mb-2" data-id="element-3071">
                      Time of Birth
                    </label>
                    <select id="timeOfBirthOption" value={formData.timeOfBirthOption} onChange={e => handleInputChange('timeOfBirthOption', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3072">
                      <option value="" data-id="element-3073">Select option</option>
                      <option value="exact" data-id="element-3074">Exact</option>
                      <option value="approximate" data-id="element-3075">Approximate</option>
                      <option value="unknown" data-id="element-3076">Unknown</option>
                    </select>
                  </div>

                  {(formData.timeOfBirthOption === 'exact' || formData.timeOfBirthOption === 'approximate') && <div data-id="element-3077">
                      <label htmlFor="timeOfBirth" className="block text-sm font-medium mb-2" data-id="element-3078">
                        Time
                      </label>
                      <input type="time" id="timeOfBirth" value={formData.timeOfBirth} onChange={e => handleInputChange('timeOfBirth', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3079" />
                    </div>}

                  <div data-id="element-3080">
                    <label className="block text-sm font-medium mb-2" data-id="element-3081">
                      Place of Birth
                    </label>
                    <input type="text" id="birthCity" value={formData.birthCity} onChange={e => handleInputChange('birthCity', e.target.value)} placeholder="City" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors mb-4" data-id="element-3082" />
                    <input type="text" id="birthCountry" value={formData.birthCountry} onChange={e => handleInputChange('birthCountry', e.target.value)} placeholder="Country" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3083" />
                  </div>
                </div>

                <button onClick={handleNext} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3084">
                  Continue
                </button>
              </div>}

            {/* Partner Baseline - Step 1: Name */}
            {currentPhase === 'partner-baseline' && currentStep === 1 && <div data-id="element-3085">
                <h1 className="text-3xl md:text-4xl font-medium mb-4 text-center leading-tight tracking-tight" data-id="element-3086">
                  Partner baseline
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-3087">
                  This establishes the interaction reference.
                  <br data-id="element-3088" />
                  It is not used for prediction or compatibility scoring.
                </p>
                <div className="mb-12" data-id="element-3089">
                  <label htmlFor="partnerName" className="block text-sm font-medium mb-3" data-id="element-3090">
                    Partner name (or initials)
                  </label>
                  <input type="text" id="partnerName" value={formData.partnerName} onChange={e => handleInputChange('partnerName', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" placeholder="Enter name or initials" data-id="element-3091" />
                </div>
                <button onClick={handleNext} disabled={!formData.partnerName} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3092">
                  Continue
                </button>
              </div>}

            {/* Partner Baseline - Step 2: Date and Time of Birth */}
            {currentPhase === 'partner-baseline' && currentStep === 2 && <div data-id="element-3093">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3094">
                  Partner baseline
                </h1>
                <div className="space-y-6 mb-12" data-id="element-3095">
                  <div data-id="element-3096">
                    <label htmlFor="partnerDob" className="block text-sm font-medium mb-3" data-id="element-3097">
                      Date of birth
                    </label>
                    <input type="date" id="partnerDob" value={formData.partnerDob} onChange={e => handleInputChange('partnerDob', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3098" />
                  </div>

                  <div data-id="element-3099">
                    <label htmlFor="partnerTimeOfBirthOption" className="block text-sm font-medium mb-3" data-id="element-3100">
                      Time of birth
                    </label>
                    <select id="partnerTimeOfBirthOption" value={formData.partnerTimeOfBirthOption} onChange={e => handleInputChange('partnerTimeOfBirthOption', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors mb-3" data-id="element-3101">
                      <option value="" data-id="element-3102">Select option</option>
                      <option value="exact" data-id="element-3103">Exact</option>
                      <option value="approximate" data-id="element-3104">Approximate</option>
                      <option value="unknown" data-id="element-3105">Unknown</option>
                    </select>
                    {(formData.partnerTimeOfBirthOption === 'exact' || formData.partnerTimeOfBirthOption === 'approximate') && <input type="time" value={formData.partnerTimeOfBirth} onChange={e => handleInputChange('partnerTimeOfBirth', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3106" />}
                  </div>
                </div>
                <button onClick={handleNext} disabled={!formData.partnerDob || !formData.partnerTimeOfBirthOption} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3107">
                  Continue
                </button>
              </div>}

            {/* Partner Baseline - Step 3: Place of Birth */}
            {currentPhase === 'partner-baseline' && currentStep === 3 && <div data-id="element-3108">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3109">
                  Partner baseline
                </h1>
                <div className="mb-12 space-y-4" data-id="element-3110">
                  <div data-id="element-3111">
                    <label htmlFor="partnerBirthCity" className="block text-sm font-medium mb-3" data-id="element-3112">
                      Place of birth
                    </label>
                    <input type="text" id="partnerBirthCity" value={formData.partnerBirthCity} onChange={e => handleInputChange('partnerBirthCity', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" placeholder="City" data-id="element-3113" />
                  </div>
                  <div data-id="element-3114">
                    <input type="text" id="partnerBirthCountry" value={formData.partnerBirthCountry} onChange={e => handleInputChange('partnerBirthCountry', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" placeholder="Country" data-id="element-3115" />
                  </div>
                </div>
                <button onClick={handleNext} disabled={!formData.partnerBirthCity || !formData.partnerBirthCountry} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3116">
                  Continue
                </button>
              </div>}

            {/* Relationship Context */}
            {currentPhase === 'relationship-context' && <div data-id="element-3117">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3118">
                  What best describes this relationship?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3119">
                  {relationshipTypeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.relationshipType === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3120">
                      <input type="radio" name="relationshipType" value={option} checked={formData.relationshipType === option} onChange={e => handleInputChange('relationshipType', e.target.value)} className="sr-only" data-id="element-3121" />
                      <span className="text-base" data-id="element-3122">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.relationshipType} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3123">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q1: Trigger */}
            {currentPhase === 'friction-loop' && currentStep === 1 && <div data-id="element-3124">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3125">
                  What typically activates tension between you?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3126">
                  {triggerOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.trigger === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3127">
                      <input type="radio" name="trigger" value={option} checked={formData.trigger === option} onChange={e => handleInputChange('trigger', e.target.value)} className="sr-only" data-id="element-3128" />
                      <span className="text-base" data-id="element-3129">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.trigger} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3130">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q2: Your Response */}
            {currentPhase === 'friction-loop' && currentStep === 2 && <div data-id="element-3131">
                <h1 className="text-3xl md:text-4xl font-medium mb-6 text-center leading-tight tracking-tight" data-id="element-3132">
                  What do you typically do when this happens?
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-3133">
                  Select up to 2 responses
                </p>
                <div className="space-y-4 mb-12" data-id="element-3134">
                  {yourResponseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.yourResponse.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3135">
                      <input type="checkbox" name="yourResponse" value={option} checked={formData.yourResponse.includes(option)} onChange={e => handleMultiSelect('yourResponse', e.target.value)} className="sr-only" data-id="element-3136" />
                      <span className="text-base" data-id="element-3137">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={formData.yourResponse.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3138">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q3: Their Response */}
            {currentPhase === 'friction-loop' && currentStep === 3 && <div data-id="element-3139">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3140">
                  How do they usually respond?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3141">
                  {theirResponseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.theirResponse === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3142">
                      <input type="radio" name="theirResponse" value={option} checked={formData.theirResponse === option} onChange={e => handleInputChange('theirResponse', e.target.value)} className="sr-only" data-id="element-3143" />
                      <span className="text-base" data-id="element-3144">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.theirResponse} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3145">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q4: Outcome */}
            {currentPhase === 'friction-loop' && currentStep === 4 && <div data-id="element-3146">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3147">
                  What usually happens after?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3148">
                  {outcomeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.outcome === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3149">
                      <input type="radio" name="outcome" value={option} checked={formData.outcome === option} onChange={e => handleInputChange('outcome', e.target.value)} className="sr-only" data-id="element-3150" />
                      <span className="text-base" data-id="element-3151">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.outcome} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3152">
                  Continue
                </button>
              </div>}

            {/* System Stability - Q5: Duration */}
            {currentPhase === 'system-stability' && currentStep === 1 && <div data-id="element-3153">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3154">
                  How long has this dynamic been present?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3155">
                  {durationOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.duration === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3156">
                      <input type="radio" name="duration" value={option} checked={formData.duration === option} onChange={e => handleInputChange('duration', e.target.value)} className="sr-only" data-id="element-3157" />
                      <span className="text-base" data-id="element-3158">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.duration} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3159">
                  Continue
                </button>
              </div>}

            {/* System Stability - Q6: Underlying Pressure */}
            {currentPhase === 'system-stability' && currentStep === 2 && <div data-id="element-3160">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3161">
                  What feels most active underneath this dynamic?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3162">
                  {underlyingPressureOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.underlyingPressure === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3163">
                      <input type="radio" name="underlyingPressure" value={option} checked={formData.underlyingPressure === option} onChange={e => handleInputChange('underlyingPressure', e.target.value)} className="sr-only" data-id="element-3164" />
                      <span className="text-base" data-id="element-3165">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.underlyingPressure} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3166">
                  Continue
                </button>
              </div>}
          </div>
        </main>
      </div>
    </>;
}